/**
 * `.m-row` — the white card a fixture is selected, expanded and acted on in.
 *
 * Three screens draw it over three payloads: Edit › Fixtures' attached fixtures
 * (`attached-fixture-row.tsx`, CAE-1550), the Add Fixtures modal's catalogue
 * fixtures (`catalogue-fixture-row.tsx`, CAE-1551) and the Add Outcomes modal's
 * grouping rows (`reels/fixture-pick-row.tsx`, CAE-1553). What they share is the
 * card itself — its border and highlighted state, the head's geometry, and the
 * band an expanded row opens. What differs is only what goes in the slots, so
 * the slots are props and the chrome is here.
 *
 * In the kit rather than beside one screen because of that third consumer: it
 * lives under `pages/contests/edit/reels/`, and a row reaching sideways into a
 * sibling screen's folder for its chrome is how one screen's refactor breaks
 * another's. Purely presentational, so `#/gallery` can still sign it off.
 *
 * > **Where the affordances sit is the caller's call.** The two Fixtures
 * > screens lead with a selection checkbox and close with the expand chevron.
 * > Add Outcomes selects *outcomes*, not fixtures, so its row has no checkbox at
 * > all and leads with the chevron instead — `.m-row-chev-left{margin-right:-4px}`
 * > in modals.css exists for exactly that variant. Hence `selection` being
 * > optional and `chevron` choosing an end, rather than two near-identical
 * > cards.
 *
 * > **This is the catalogue's row, not the Overview's.** `components/ui/
 * > fixture-row.tsx` implements the other one: `.fixture` is an inset tile on
 * > `--surface-muted` with a dotless badge, for a dashboard. The comparison is
 * > in `docs/design/admin-ui/prototype/README.md`.
 *
 * Three things differ from the prototype, all of them CAE-1550's acceptance
 * criteria rather than taste:
 *
 * - **The checkbox is reachable.** The prototype's `CB` is a `<span>` wrapping
 *   a `tabIndex={-1}` input, so the whole selection model is mouse-only. This
 *   is a real focusable checkbox.
 * - **Hover-revealed controls are always in the tab order**, though
 *   `.fx-row-remove` only reveals them on hover. Opacity is a pointer
 *   affordance; a control that exists only while the mouse is over it does not
 *   exist for the keyboard.
 * - **Neither the checkbox nor those controls expands the row.** All of them
 *   stop the click before it reaches the row body, which is what the row
 *   listens on.
 */
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import type { Theme } from '@mui/material/styles';
import { ChevronDownIcon, ChevronRightIcon } from './line-icons';
import { Crests, type CrestTeam } from './crests';
import { FixtureSelectCheckbox } from './fixture-select-checkbox';

export interface FixtureCardProps {
  testId: string;
  home: CrestTeam;
  /** Omitted for a tournament fixture, which has no opposing side. */
  away?: CrestTeam;
  title: string;
  /**
   * Sits immediately after the title: the rename pencil on an attached row, the
   * "Already added" tag on a catalogue one. A control put here that carries
   * `data-row-affordance` is revealed on hover and on focus.
   */
  titleAdornment?: React.ReactNode;
  /** The clauses under the title, usually `<FixtureMetaClauses>`. */
  meta: React.ReactNode;
  /** The right-hand group before the chevron: pills, badges, row actions. */
  side?: React.ReactNode;
  /**
   * The leading checkbox, when the row is one of the things being selected.
   *
   * Omitted by Add Outcomes, whose selection lives on the outcomes nested
   * inside the row rather than on the row itself.
   */
  selection?: {
    selected: boolean;
    onSelectedChange: (selected: boolean) => void;
    /** The checkbox's accessible name, e.g. "Select Mexico vs South Africa". */
    label: string;
    disabled?: boolean;
  };
  /**
   * Which end the expand chevron sits at. `leading` takes the space a row with
   * no checkbox leaves at its head.
   */
  chevron?: 'leading' | 'trailing';
  /**
   * Draws the accent border and inset ring. Defaults to whether the row is
   * selected; Add Outcomes passes it for "has picks under it" instead
   * (`.m-row.is-haspicks`, which the stylesheet draws identically).
   */
  highlighted?: boolean;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  /** What the chevron names when it says "Expand markets for …". */
  expandLabel: string;
  /** The expand band. Rendered only while `expanded`. */
  children?: React.ReactNode;
}

export const FixtureCard = ({
  testId,
  home,
  away,
  title,
  titleAdornment,
  meta,
  side,
  selection,
  chevron = 'trailing',
  highlighted,
  expanded,
  onExpandedChange,
  expandLabel,
  children,
}: FixtureCardProps) => {
  const lit = highlighted ?? selection?.selected ?? false;
  const chevronButton = (
    <IconButton
      size="small"
      aria-expanded={expanded}
      aria-label={
        expanded ? `Collapse markets for ${expandLabel}` : `Expand markets for ${expandLabel}`
      }
      onClick={(event) => {
        event.stopPropagation();
        onExpandedChange(!expanded);
      }}
      sx={(theme: Theme) => ({
        width: '28px',
        height: '28px',
        // `.m-row-chev-left{margin-right:-4px}` — closes the gap the missing
        // checkbox would otherwise leave before the crests.
        ...(chevron === 'leading' ? { marginRight: '-4px' } : {}),
        borderRadius: `${theme.tokens.radius.control}px`,
        color: theme.tokens.color.ink4,
        '&:hover': {
          backgroundColor: theme.tokens.color.bg,
          color: theme.tokens.color.ink2,
        },
      })}
    >
      {expanded ? <ChevronDownIcon fontSize="inherit" /> : <ChevronRightIcon fontSize="inherit" />}
    </IconButton>
  );

  return (
    <Box
      data-testid={testId}
      sx={(theme: Theme) => ({
        backgroundColor: theme.tokens.color.surface,
        border: `${theme.tokens.border.width} solid ${
          lit ? theme.tokens.color.accent : theme.tokens.color.lineStrong
        }`,
        borderRadius: `${theme.tokens.radius.insetTile}px`,
        transition: 'border-color 0.15s, box-shadow 0.15s, background 0.15s',
        ...(lit
          ? { boxShadow: `0 0 0 1px ${theme.tokens.color.accentSoft} inset` }
          : { '&:hover': { borderColor: theme.tokens.componentColor.metaSeparator } }),
        /*
         * `.fx-row-remove` reveals its control on hover — and on focus, so it is
         * visible to whoever reached it with the keyboard.
         */
        [`&:hover [data-row-affordance], & [data-row-affordance]:focus-visible`]: {
          opacity: 1,
        },
      })}
    >
      <Box
        onClick={() => onExpandedChange(!expanded)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '14px',
          padding: '14px 16px',
          cursor: 'default',
        }}
      >
        {chevron === 'leading' ? chevronButton : null}

        {selection ? (
          <FixtureSelectCheckbox
            checked={selection.selected}
            onChange={selection.onSelectedChange}
            label={selection.label}
            disabled={selection.disabled ?? false}
          />
        ) : null}

        <Crests home={home} away={away} />

        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
            <Box
              component="span"
              sx={(theme: Theme) => ({
                fontSize: '14.5px',
                fontWeight: 600,
                lineHeight: 1.25,
                letterSpacing: '-0.005em',
                color: theme.tokens.color.ink,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              })}
            >
              {title}
            </Box>
            {titleAdornment}
          </Box>

          <Box
            sx={(theme: Theme) => ({
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexWrap: 'wrap',
              fontSize: '12.5px',
              color: theme.tokens.color.ink3,
              fontVariantNumeric: 'tabular-nums',
            })}
          >
            {meta}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
          {side}
          {chevron === 'trailing' ? chevronButton : null}
        </Box>
      </Box>

      {expanded ? children : null}
    </Box>
  );
};
